import styles from './PFPBuilder.module.css';
import { useState, useEffect, useRef } from 'react';

function PFPBuilder() {
  /* Constants */
  /* ---------------------------------------------------------------------- */

  const layers = {
    base: [
      'rock',
      'circus',
      'electro',
      'ambient',
      'blues',
      'jazz',
      'latin',
      'hiphop',
      'spokenword',
      'acapella',
      'folk',
      'country',
      'russianmarch',
      'doowop',
      'metal',
      'punk',
    ],
    beard: ['none', 'goatee', 'beard', 'shadow'],
    bottom: [
      'vocals',
      'samples',
      'piano',
      'organ',
      'bass',
      'drummachine',
      'electricguitar',
      'ukulele',
      'baratoneuke',
      'fakehorns',
      'harpsichord',
      'banjo',
      'synth',
      'guitar',
      'acousticguitar',
    ],
    glasses: ['none', 'hiphop', 'spiral', 'wavy', 'lightning', 'round', 'plain'],
    head: [
      'commission',
      'wifi',
      'food',
      'family',
      'podcast',
      'conference',
      'television',
      'grandma',
      'animal',
      'improv',
      'love',
      'friend',
      'videogames',
      'weather',
      'music',
      'christmas',
      'motivation',
      'politics',
      'sport',
      'object',
      'remix',
      'popculture',
      'instrumental',
      'wanderingfall',
      'socialjustice',
      'traditional',
      'songaday',
      'hero',
      'kids',
      'harrypotter',
      'psa',
      'poetry',
      'childhood',
      'life',
      'nerd',
      'chatroulette',
      'noticeme',
      'sick',
      'sex',
      'ringtone',
      'contest',
      'bike',
      'science',
      'airport',
      'book',
      'apple',
    ],
    mood: [
      'sad',
      'angry',
      'chill',
      'excited',
      'silly',
      'happy',
      'hopeful',
      'confused',
      'pensive',
      'anxious',
      'intense',
      'bored',
      'drunk',
      'tired',
    ],
    top: [
      'seattle',
      'vermont',
      'la',
      'hawaii',
      'jupiter',
      'bennington',
      'southington',
      'berkeley',
      'vienna',
      'oakland',
      'baltimore',
      'newyork',
      'berkeleystudio',
      'pensacola',
      'portland',
      'sandiego',
      'sanfrancisco',
    ],
  };

  /* Ref */
  /* ---------------------------------------------------------------------- */

  const firstUpdate = useRef(true);

  /* State */
  /* ---------------------------------------------------------------------- */

  const [step, setStep] = useState('base');

  const [layerBase, setLayerBase] = useState('');

  const [layerBeard, setLayerBeard] = useState('none');

  const [layerBottom, setLayerBottom] = useState('');

  const [layerGlasses, setLayerGlasses] = useState('none');

  const [layerHead, setLayerHead] = useState('');

  const [layerMood, setLayerMood] = useState('');

  const [layerTop, setLayerTop] = useState('');

  /* Init */
  /* ---------------------------------------------------------------------- */

  function init() {
    restoreAllLayerCache();
  }

  /* Layer Cache */
  /* ---------------------------------------------------------------------- */

  function getLayerCacheId(position: string) {
    return `songaday-pfp-layer-${position}`;
  }

  function setLayerCache(position: string, name: string) {
    const cacheId = getLayerCacheId(position);

    localStorage.setItem(cacheId, name);
  }

  function getLayerCache(position: string): string {
    const cacheId = getLayerCacheId(position);

    return localStorage.getItem(cacheId);
  }

  function restoreLayerCache(position: string) {
    const cacheValue = getLayerCache(position);

    if (cacheValue) {
      setLayer(position, cacheValue);
    }
  }

  function restoreAllLayerCache() {
    restoreLayerCache('base');
    restoreLayerCache('beard');
    restoreLayerCache('bottom');
    restoreLayerCache('glasses');
    restoreLayerCache('head');
    restoreLayerCache('mood');
    restoreLayerCache('top');
  }

  /* Layers */
  /* ---------------------------------------------------------------------- */

  function setLayer(position: string, name: string) {
    setLayerCache(position, name);

    switch (position) {
      case 'base':
        return setLayerBase(name);
      case 'beard':
        return setLayerBeard(name);
      case 'bottom':
        return setLayerBottom(name);
      case 'glasses':
        return setLayerGlasses(name);
      case 'head':
        return setLayerHead(name);
      case 'mood':
        return setLayerMood(name);
      case 'top':
        return setLayerTop(name);
    }
  }

  function getLayer(position: string): string {
    switch (position) {
      case 'base':
        return layerBase;
      case 'beard':
        return layerBeard;
      case 'bottom':
        return layerBottom;
      case 'glasses':
        return layerGlasses;
      case 'head':
        return layerHead;
      case 'mood':
        return layerMood;
      case 'top':
        return layerTop;
    }

    return '';
  }

  function randomLayer(position: string) {
    const randomIndex = Math.floor(Math.random() * layers[position].length);
    const randomLayer = layers[position][randomIndex];

    setLayer(position, randomLayer);
  }

  function resetLayers() {
    setLayer('base', '');
    setLayer('beard', 'none');
    setLayer('bottom', '');
    setLayer('glasses', 'none');
    setLayer('head', '');
    setLayer('mood', '');
    setLayer('top', '');
  }

  function randomLayers() {
    randomLayer('base');
    randomLayer('beard');
    randomLayer('bottom');
    randomLayer('glasses');
    randomLayer('head');
    randomLayer('mood');
    randomLayer('top');
  }

  /* Conditions */
  /* ---------------------------------------------------------------------- */

  function hasAllRequiredLayers(): boolean {
    return (
      layerBase !== '' &&
      // layerBeard !== '' &&
      layerBottom !== '' &&
      // layerGlasses !== '' &&
      layerHead !== '' &&
      layerMood !== '' &&
      layerTop !== ''
    );
  }

  function isThumbSelected(position: string, name: string): boolean {
    return getLayer(position) === name;
  }

  function isStepActive(stepToCheck: string): boolean {
    return step === stepToCheck;
  }

  function canMint(): boolean {
    return hasAllRequiredLayers();
  }

  /* CSS Classes */
  /* ---------------------------------------------------------------------- */

  function getThumbSelectedStatus(position: string, name: string) {
    return isThumbSelected(position, name) === true ? 'active' : 'inactive';
  }

  function getStepStatus(step: string): string {
    return isStepActive(step) === true ? 'active' : 'inactive';
  }

  /* Actions */
  /* ---------------------------------------------------------------------- */

  function stepClick(name: string) {
    setStep(name);
  }

  function resetClick() {
    resetLayers();
  }

  function randomClick() {
    randomLayers();
  }

  function mintClick() {
    if (hasAllRequiredLayers() === false) {
      console.log('complete pfp before mint');
      return;
    }

    console.log('mint');
  }

  /* Components */
  /* ---------------------------------------------------------------------- */

  function PreviewLayer(props: { position: string; ext: string }) {
    const imgName = getLayer(props.position);

    return (
      imgName && (
        <img
          className={styles['pfp-builder-preview__layer']}
          src={`/pfp/preview/${props.position}/${imgName}.${props.ext}`}
          alt={`Layer: ${imgName}`}
        ></img>
      )
    );
  }

  function StepButton(props: { step: string; name: string }) {
    return (
      <li
        className={`
          ${styles['pfp-builder-controls-nav__entry']}
          ${styles[`pfp-builder-controls-nav__entry--${getStepStatus(props.step)}`]}
        `}
      >
        <button
          className={styles['pfp-builder-controls-nav__entry-button']}
          onClick={() => stepClick(props.step)}
        >
          {props.name}
        </button>
      </li>
    );
  }

  function Step(props: { step: string; children: any }) {
    return (
      <section
        className={`
          ${styles['pfp-builder-controls-step']}
          ${styles[`pfp-builder-controls-step--${getStepStatus(props.step)}`]}
        `}
      >
        {props.children}
      </section>
    );
  }

  function StepThumbnails(props: { name: string; children: any }) {
    return (
      <section className={styles['pfp-builder-controls-step-thumbnails']}>
        {props.name && (
          <h5 className={styles['pfp-builder-controls-step-thumbnails__subheader']}>
            {props.name}
          </h5>
        )}
        <div className={styles['pfp-builder-controls-step-thumbnails__thumbnails']}>
          {props.children}
        </div>
      </section>
    );
  }

  function Thumbnails(props: { position: string; name: string; ext: string }) {
    return (
      <ul className={styles['pfp-builder-thumbnails']}>
        {layers[props.position].map((imgName, index) => (
          <li
            className={`
              ${styles['pfp-builder-thumbnails__thumbnail']}
              ${
                styles[
                  `pfp-builder-thumbnails__thumbnail--${getThumbSelectedStatus(
                    props.position,
                    imgName,
                  )}`
                ]
              }
            `}
            key={index}
          >
            <img
              loading="eager"
              className={styles['pfp-builder-thumbnails__thumbnail-placeholder']}
              src="/pfp/thumb/placeholder.png"
              alt={`${props.name}: placeholder`}
            ></img>
            <img
              loading="lazy"
              className={styles['pfp-builder-thumbnails__thumbnail-img']}
              src={`/pfp/thumb/${props.position}/${imgName}.${props.ext}`}
              alt={`${props.name}: ${imgName}`}
              onClick={() => setLayer(props.position, imgName)}
            ></img>
          </li>
        ))}
      </ul>
    );
  }

  /* Bootstrap */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (firstUpdate.current === false) {
      return;
    }

    if (firstUpdate.current) {
      firstUpdate.current = false;
    }

    // Init
    init();
  });

  /* Main Component */
  /* ---------------------------------------------------------------------- */

  return (
    <div className={styles['pfp-builder']}>
      <div className={styles['pfp-builder__preview']}>
        <div className={styles['pfp-builder-preview']}>
          <img
            className={styles['pfp-builder-preview__placeholder']}
            src="/pfp/preview/placeholder.png"
            alt="Placeholder"
          ></img>
          <PreviewLayer position="base" ext="jpg"></PreviewLayer>
          <PreviewLayer position="head" ext="png"></PreviewLayer>
          <PreviewLayer position="mood" ext="png"></PreviewLayer>
          <PreviewLayer position="beard" ext="png"></PreviewLayer>
          <PreviewLayer position="glasses" ext="png"></PreviewLayer>
          <PreviewLayer position="top" ext="png"></PreviewLayer>
          <PreviewLayer position="bottom" ext="png"></PreviewLayer>
        </div>
      </div>
      <div className={styles['pfp-builder__controls']}>
        <div className={styles['pfp-builder-controls']}>
          <header className={styles['pfp-builder-controls__header']}>
            <button
              className={`
                ${styles['pfp-builder-controls__header-button']}
                ${styles['pfp-builder-controls__header-button--reset']}
              `}
              onClick={() => resetClick()}
            >
              Reset
            </button>
            <button
              className={`
                ${styles['pfp-builder-controls__header-button']}
                ${styles['pfp-builder-controls__header-button--random']}
              `}
              onClick={() => randomClick()}
            >
              Random
            </button>
            <button
              className={`
                ${styles['pfp-builder-controls__header-button']}
                ${styles['pfp-builder-controls__header-button--mint']}
              `}
              onClick={() => mintClick()}
              disabled={!canMint()}
            >
              ★ Mint PFP
            </button>
          </header>
          <main className={styles['pfp-builder-controls__main']}>
            <nav className={styles['pfp-builder-controls__nav']}>
              <ul className={styles['pfp-builder-controls-nav']}>
                <StepButton step="base" name="Base"></StepButton>
                <StepButton step="head" name="Head"></StepButton>
                <StepButton step="face" name="Face"></StepButton>
                <StepButton step="top" name="Top"></StepButton>
                <StepButton step="bottom" name="Bottom"></StepButton>
              </ul>
            </nav>
            <div className={styles['pfp-builder-controls__steps']}>
              <Step step="base">
                <StepThumbnails name="">
                  <Thumbnails position="base" name="Base" ext="jpg"></Thumbnails>
                </StepThumbnails>
              </Step>
              <Step step="head">
                <StepThumbnails name="">
                  <Thumbnails position="head" name="Head" ext="png"></Thumbnails>
                </StepThumbnails>
              </Step>
              <Step step="face">
                <StepThumbnails name="Face">
                  <Thumbnails position="mood" name="Mood" ext="png"></Thumbnails>
                </StepThumbnails>
                <StepThumbnails name="Glasses">
                  <Thumbnails position="glasses" name="Glasses" ext="png"></Thumbnails>
                </StepThumbnails>
                <StepThumbnails name="Beard">
                  <Thumbnails position="beard" name="Beard" ext="png"></Thumbnails>
                </StepThumbnails>
              </Step>
              <Step step="top">
                <StepThumbnails name="">
                  <Thumbnails position="top" name="Top" ext="png"></Thumbnails>
                </StepThumbnails>
              </Step>
              <Step step="bottom">
                <StepThumbnails name="">
                  <Thumbnails position="bottom" name="Bottom" ext="png"></Thumbnails>
                </StepThumbnails>
              </Step>
            </div>
          </main>
        </div>
      </div>
      <div className="pfp-builder__end"></div>
    </div>
  );
}

export default PFPBuilder;
